import { CONFIGURATION_AREAS, SEARCH_SERVICES } from '../types';

import type {
  ConfigurationArea,
  ConfigurationSearchResult,
  DeviceSearchResult,
  PlanSearchResult,
  SearchGroupType,
  SearchResult,
  SearchResultGroup,
  SearchService,
  WorkflowSearchResult,
} from '../types';
import type {
  ConfigurationSearchItem,
  DeviceSearchItem,
  PlanSearchItem,
  SearchBody,
  WorkflowSearchItem,
} from './search-contract';

const isService = (value: string | undefined): value is SearchService =>
  (SEARCH_SERVICES as readonly (string | undefined)[]).includes(value);

const isArea = (value: string): value is ConfigurationArea =>
  (CONFIGURATION_AREAS as readonly string[]).includes(value);

export function toDeviceResult(item: DeviceSearchItem): DeviceSearchResult {
  return {
    type: 'DEVICE',
    id: item.id,
    identifier: item.name,
    service: isService(item.service) ? item.service : null,
    plan: item.plan_name ?? null,
    state: item.state,
  };
}

export function toWorkflowResult(item: WorkflowSearchItem): WorkflowSearchResult {
  return {
    type: 'WORKFLOW',
    id: item.id,
    name: item.name,
    deviceCount: item.device_count,
    updatedAt: new Date(item.updated_at),
  };
}

export function toPlanResult(item: PlanSearchItem): PlanSearchResult {
  return {
    type: 'PLAN',
    id: item.id,
    name: item.name,
    service: isService(item.service) ? item.service : null,
    deviceCount: item.device_count,
  };
}

/** Null for an area this portal has no page for: a row that opens nothing is left out. */
export function toConfigurationResult(
  item: ConfigurationSearchItem,
): ConfigurationSearchResult | null {
  if (!isArea(item.area)) return null;
  return {
    type: 'CONFIGURATION',
    id: item.id,
    name: item.name,
    area: item.area,
    email: item.area === 'USERS' ? (item.email ?? null) : null,
  };
}

/** One group of the panel from its route's answer; `null` items are rows it cannot show. */
export function toGroup<TItem>(
  type: SearchGroupType,
  body: SearchBody<TItem>,
  toResult: (item: TItem) => SearchResult | null,
): SearchResultGroup {
  return {
    type,
    total: body.total,
    items: body.items.flatMap((item) => toResult(item) ?? []),
  };
}

const withQuery = (path: string, params: Record<string, string>) =>
  `${path}?${new URLSearchParams(params).toString()}`;

/** Named explicitly: device tabs are routes, and `/devices` only redirects to the registry. */
export const DEVICES_PATH = '/devices/registry';
const PLANS_PATH = '/plans-and-workflows/plans';
const WORKFLOWS_PATH = '/plans-and-workflows/workflows';
const SETTINGS_PATH = '/app-customization';

const AREA_PATHS: Record<Exclude<ConfigurationArea, 'USERS'>, string> = {
  ROLES: `${SETTINGS_PATH}/users-and-roles/roles-and-permissions`,
  WHITELIST: `${SETTINGS_PATH}/whitelist-restrictions`,
  APP_CUSTOMISATION: `${SETTINGS_PATH}/application-customisation`,
};

/**
 * Where a row opens. A device opens its side panel by id on the registry narrowed to its
 * identifier, so the list shows the one row the panel describes rather than page 1 of every
 * device. A workflow opens its side panel by id. A plan has no page of its own yet, so it opens
 * the Plans list narrowed to its name. A user opens the Users list narrowed to their email, which
 * is unique; every other area opens its settings page.
 */
export function resultHref(result: SearchResult): string {
  switch (result.type) {
    case 'DEVICE':
      return withQuery(DEVICES_PATH, { device: result.id, identifier: result.identifier });
    case 'WORKFLOW':
      return withQuery(WORKFLOWS_PATH, { workflow: result.id });
    case 'PLAN':
      return withQuery(PLANS_PATH, { search: result.name });
    case 'CONFIGURATION':
      return result.area === 'USERS'
        ? withQuery(`${SETTINGS_PATH}/users-and-roles/users`, {
            search: result.email ?? result.name,
          })
        : AREA_PATHS[result.area];
  }
}

/**
 * Where "View all" opens: the group's own list narrowed to the term. Settings have no list to
 * narrow, so their "View all" opens the settings directory.
 */
export function viewAllHref(type: SearchGroupType, query: string): string {
  const term = query.trim();
  switch (type) {
    case 'DEVICE':
      return withQuery(DEVICES_PATH, { identifier: term });
    case 'WORKFLOW':
      return withQuery(WORKFLOWS_PATH, { search: term });
    case 'PLAN':
      return withQuery(PLANS_PATH, { search: term });
    case 'CONFIGURATION':
      return SETTINGS_PATH;
  }
}
